import { WorkfrontBundleSyncStatus } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Button, Spinner } from '@atlas/ui-primitives'
import { useParams } from 'react-router-dom'
import { useRefetchWorkfrontBundle, useWorkfrontBundle, useWorkfrontHealth } from '@/hooks/useWorkfront'
import { useBundleCanonical, useRemapBundle } from '@/hooks/useWorkfrontMappings'
import { useWorkOrderSyncActions } from '@/hooks/useWorkOrderSync'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { WorkfrontBundleDetailDTO, WorkfrontBundleNodeDTO } from '@/services/workfront.service'
import { BundleTree } from './components/BundleTree'
import { ErrorsTable } from './components/ErrorsTable'
import { BundleStateBadge, SyncStatusBadge } from './components/StatusBadges'
import { SyncActionsList } from './components/SyncActionsList'
import { VersionList } from './components/VersionList'
import { CanonicalView } from './mappings/components/CanonicalView'
import { bundleTitleOf, useErrorActions, WORKFRONT_PATHS, WorkfrontHeader } from './shared'

/** Refetch, and the parent project in Workfront when the host and the parent are known. */
function BundleActions({ bundle, host }: { bundle: WorkfrontBundleDetailDTO; host: string | null }) {
	const refetch = useRefetchWorkfrontBundle()
	const fetching = bundle.syncStatus === WorkfrontBundleSyncStatus.FETCHING
	return (
		<>
			<Button
				isDisabled={fetching || refetch.isPending}
				onPress={() =>
					refetch.mutate(bundle.id, {
						onSuccess: () => notification.success(`Refetching ${bundle.title ?? 'the bundle'}.`),
						onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not refetch the bundle.')),
					})
				}
			>
				{fetching ? 'Fetching…' : 'Refetch'}
			</Button>
			{host && bundle.parentProjectId ? (
				<a
					className="inline-flex items-center rounded-ds-sm border border-ds-border px-3 text-ds-sm hover:bg-ds-surface"
					href={`https://${host}/project/${bundle.parentProjectId}`}
					target="_blank"
					rel="noreferrer"
				>
					Open in Workfront
				</a>
			) : null}
		</>
	)
}

/** Every object of the tree by Workfront ID → its name. */
function namesOf(nodes: WorkfrontBundleNodeDTO[], into: Record<string, string> = {}): Record<string, string> {
	for (const node of nodes) {
		if (node.name) into[node.workfrontId] = node.name
		namesOf(node.children, into)
	}
	return into
}

/** The canonical record the mapper built, its holds named by the bundle's open errors, and Re-map. */
function BundleCanonical({ bundle }: { bundle: WorkfrontBundleDetailDTO }) {
	const canonical = useBundleCanonical(bundle.id)
	const remap = useRemapBundle()
	const errorsById = Object.fromEntries(
		bundle.errors.map((error) => [error.id, { title: error.title, fixLink: error.fixLink }]),
	)
	return (
		<SectionCard title="Canonical record">
			{canonical.isError ? (
				<p className="text-ds-sm text-ds-danger">
					{apiErrorMessage(canonical.error, 'Could not load the canonical record.')}
				</p>
			) : (
				<CanonicalView
					record={canonical.data ?? null}
					isLoading={canonical.isLoading}
					errorsById={errorsById}
					namesById={namesOf(bundle.tree)}
					remapping={remap.isPending}
					onRemap={() =>
						remap.mutate(bundle.id, {
							onSuccess: () => notification.success('Re-map requested'),
							onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not request a re-map.')),
						})
					}
				/>
			)}
		</SectionCard>
	)
}

/** What the work-order sync did, or in shadow mode would do, to each work order of the bundle's latest version. */
function BundleSyncActions({ bundleId }: { bundleId: string }) {
	const actions = useWorkOrderSyncActions(bundleId)
	return (
		<SectionCard title="Sync actions" count={actions.data?.length}>
			{actions.isLoading ? (
				<Spinner aria-label="Loading the sync actions" />
			) : actions.isError ? (
				<p className="text-ds-sm text-ds-danger">
					{apiErrorMessage(actions.error, 'Could not load the sync actions.')}
				</p>
			) : (
				<SyncActionsList actions={actions.data ?? []} />
			)}
		</SectionCard>
	)
}

/** One bundle: its tree, its canonical record, the sync's actions, its versions with their change lists, and its open problems. */
export function WorkfrontBundleDetailPage() {
	const { id = '' } = useParams()
	const query = useWorkfrontBundle(id)
	const health = useWorkfrontHealth()
	const { actions, dialog } = useErrorActions()
	const bundle = query.data

	if (query.isLoading) return <Spinner aria-label="Loading the bundle" />
	if (!bundle) {
		return (
			<div className="space-y-6">
				<WorkfrontHeader title="Bundle" />
				<p className="text-ds-sm text-ds-danger">{apiErrorMessage(query.error, 'Could not load this bundle.')}</p>
			</div>
		)
	}
	return (
		<div className="space-y-6">
			<WorkfrontHeader
				title={bundleTitleOf(bundle)}
				actions={<BundleActions bundle={bundle} host={health.data?.host ?? null} />}
			/>
			<div className="flex flex-wrap items-center gap-2 text-ds-sm">
				<BundleStateBadge state={bundle.state} />
				<SyncStatusBadge status={bundle.syncStatus} />
				{bundle.referenceNumber ? <span>#{bundle.referenceNumber}</span> : null}
				<span className="text-ds-text-muted">
					version {bundle.version} · {bundle.childCount} asset briefs · program {bundle.programId}
				</span>
			</div>
			<SectionCard title="Tree">
				<BundleTree nodes={bundle.tree} />
			</SectionCard>
			<BundleCanonical bundle={bundle} />
			<BundleSyncActions bundleId={bundle.id} />
			<SectionCard title="Versions" count={bundle.versions.length}>
				<VersionList versions={bundle.versions} />
			</SectionCard>
			<SectionCard title="Open problems" count={bundle.errors.length}>
				<ErrorsTable
					errors={bundle.errors}
					hrefOf={(error) => WORKFRONT_PATHS.error(error.id)}
					emptyDescription="Nothing is wrong with this bundle."
					{...actions}
				/>
			</SectionCard>
			{dialog}
		</div>
	)
}
